import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@/components/ui/Tooltip';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { Notice } from '@/components/ui/Notice';
import { ContactFormModal } from '@/components/contacts/ContactFormModal';
import { ContactTitleInlineField } from '@/components/contacts/ContactTitleInput';
import { MarketingHistoryPanel } from '@/components/marketing/MarketingHistoryPanel';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { usePageLayout, type LayoutBlockDefault } from '@/lib/use-page-layout';
import {
  InlineField,
  type InlineFieldOption,
  type InlineSaveResult,
} from '@/components/ui/InlineField';
import { ApiError, api } from '@/lib/api';
import {
  CONTACT_NAME_LABELS,
  applyNameEdit,
  splitFullName,
  type ContactNameField,
  type ContactNameParts,
} from '@/lib/contact-name';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime, orDash, toDateInputValue } from '@/lib/format';
import { leadSourceLabel } from '@/lib/lead-source';
import { mailtoHref } from '@/lib/mail';
import { denialReason } from '@/lib/permissions';
import type { Contact, ContactInput, UserSummary } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import { USER_GROUP_SLUGS, narrowingNote, outsideGroupNote } from '@/lib/user-groups';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
} from '@/components/ui';

/* --- icons (Lucide paths, hand-inlined — no icon library) ---------------- */

function iconProps(className: string) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className,
  };
}

function ChevronLeftIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function ContactIcon() {
  return (
    <svg {...iconProps('h-5 w-5')}>
      <path d="M18 21a6 6 0 0 0-12 0" />
      <circle cx="12" cy="8" r="5" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
    </svg>
  );
}

/* --- link helpers -------------------------------------------------------- */

/**
 * `tel:` tolerates spaces and punctuation poorly, so strip everything a dialler
 * cannot use. The visible text stays exactly as it was entered.
 */
function telHref(countryCode: string | null, value: string): string {
  const digits = `${countryCode ?? ''}${value}`.replace(/[^\d+]/g, '');
  return `tel:${digits}`;
}

/**
 * The API validates website/linkedin by length only, so "acme.edu" is a legal
 * stored value. A bare host with no scheme would resolve relative to this SPA
 * and 404, so assume https when none is present.
 */
function externalHref(value: string): string {
  return /^[a-z][a-z\d+\-.]*:/i.test(value) ? value : `https://${value}`;
}

/**
 * The mail / dial / open affordance for a value that is now edited in place.
 *
 * ⚠️ It goes in `InlineField`'s `action` slot, NEVER in `display`. The idle
 * state of an editable field is a `<button>`, and an anchor cannot nest inside
 * one — so the value renders as plain text under the trigger and the link sits
 * beside it. That is the whole reason these three used to be `EmailValue` /
 * `PhoneValue` / `UrlValue` wrapping the text and no longer can.
 */
function ValueAction({
  value,
  kind,
  countryCode,
}: {
  value: string | null;
  kind: 'email' | 'tel' | 'url';
  countryCode?: string | null;
}) {
  if (!value || value.trim() === '') return null;
  const trimmed = value.trim();
  const href =
    kind === 'email'
      ? mailtoHref(trimmed)
      : kind === 'tel'
        ? telHref(countryCode ?? null, trimmed)
        : externalHref(trimmed);
  const label =
    kind === 'email' ? `Email ${trimmed}` : kind === 'tel' ? `Call ${trimmed}` : `Open ${trimmed}`;
  /*
   * Only `url` opens a new tab. *SUPERSEDED 2026-10-16: `email` joined it while
   * the mail link was a Gmail compose URL (2026-09-18).* The owner chose a real
   * `mailto:` again, which, like `tel:`, is handed to another application and
   * leaves this page alone.
   */
  const opensElsewhere = kind === 'url';

  return (
    <Tooltip label={label}>
      <a
        href={href}
        aria-label={label}
        {...(opensElsewhere ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        className={cx(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-subtle max-sm:h-9 max-sm:w-9',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:bg-surface-hover hover:text-fg',
        )}
      >
        {kind === 'email' ? <MailIcon /> : kind === 'tel' ? <PhoneIcon /> : <ExternalIcon />}
      </a>
    </Tooltip>
  );
}

/** `linked` only when the value is NOT inside InlineField's trigger button (no nested anchors). */
function StaffValue({ user, linked }: { user: UserSummary | null; linked: boolean }) {
  if (!user) return <span className="text-fg-subtle">Unassigned</span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <Avatar name={user.name} size="sm" />
      <span className="min-w-0">
        <span className="block truncate text-sm text-fg">{user.name}</span>
        {linked && user.email !== '' ? (
          <EmailLink address={user.email} tone="muted" className="block text-xs" />
        ) : (
          <span className="block text-xs text-fg-muted [overflow-wrap:anywhere]">{user.email}</span>
        )}
      </span>
    </span>
  );
}

/**
 * A contact with no company is a normal state, not an error: deleting a company
 * sets the FK to null rather than cascading. It gets a labelled Badge so the
 * slot never reads as a broken link or a gap in the record.
 */
function CompanyValue({ company }: { company: Contact['company'] }) {
  if (!company)
    return (
      <Badge tone="neutral" variant="outline">
        No account
      </Badge>
    );
  return (
    <TextLink className="[overflow-wrap:anywhere]" to={`/companies/${company.id}`}>
      {company.name}
    </TextLink>
  );
}

/** Explicit words, never colour alone — a red dot on its own says nothing. */
function OptOutValue({ optedOut }: { optedOut: boolean }) {
  return (
    <Badge tone={optedOut ? 'warning' : 'success'} dot>
      {optedOut ? 'Opted out of email' : 'Subscribed to email'}
    </Badge>
  );
}

/* --- editing in place: the write contract -------------------------------- *
 *
 * `ContactFormModal` stays — inline is the fast path for one column, the modal
 * is better for many at once. What must NOT diverge is the rules, so every
 * limit and every format check below is COPIED from that modal's `LIMITS` and
 * `validate()`. Two rules for one column is how they drift.
 *
 * `PATCH /contacts/:id` is `.strict()`: an unknown key is a 422 naming it under
 * `key`, not a silent strip. An omitted key is untouched and an explicit `null`
 * clears the column, so one field sends ONE key and never a spread of the
 * loaded record.
 *
 * ⚠️ Two columns are deliberately NOT edited here:
 *
 *  - **`companyId`** — re-parenting needs the debounced server-side account
 *    search that lives in the modal, and it moves the derived `accountLead`.
 *  - **`leadSource`** — see the Lead source row. It is the deprecated free
 *    text; the screen displays the resolved `leadSourceRef`, and binding an
 *    editor to what is displayed would write the category over somebody's
 *    typed detail. `ContactInput` carries no `leadSourceId` key to write
 *    instead, so there is nothing correct to edit here yet.
 *
 * ⚠️ The three name fields are the one place a single field sends SEVERAL keys:
 * they fill each other BOTH ways through `applyNameEdit` (`@/lib/contact-name`,
 * owner 2026-10-16) — see `nameEditPatch`.
 */

/**
 * Max lengths, mirrored from `ContactFormModal`'s `LIMITS` exactly — no
 * stricter, no looser. An inline editor that refuses what the modal accepts is
 * a rule the API never had; one that accepts what the modal refuses is a 422
 * the person only meets after typing.
 *
 * `name` and `layout` are in this table for their length rule alone: both are
 * `NOT NULL` server-side, so neither is ever sent as `null` — see
 * `contactPatch`. `leadSource` is deliberately absent, because nothing here
 * edits it and an unreachable branch is worse than a missing one.
 */
const TEXT_MAX = {
  name: 240,
  firstName: 120,
  lastName: 120,
  layout: 60,
  title: 120,
  contactRole: 120,
  department: 120,
  email: 255,
  secondaryEmail: 255,
  countryCode: 8,
  mobile: 32,
  phone: 32,
  website: 255,
  linkedin: 255,
  billingStreet: 255,
  billingCity: 120,
  billingState: 120,
  billingCode: 32,
  billingCountry: 120,
  shippingStreet: 255,
  shippingCity: 120,
  shippingState: 120,
  shippingCode: 32,
  shippingCountry: 120,
  description: 20000,
} as const;

type TextKey = keyof typeof TEXT_MAX;

/**
 * Read as `accountLead` / `clientSuccessManager`, written as `…Id`.
 *
 * ⚠️ **`ownerId` was a third member until 2026-08-31 and its absence is the
 * guard.** The owner retired the field on contacts with the other four
 * entities; `contact.owner` is still read back by the API and this page no
 * longer renders or writes it. Because `staffPatch` below is the ONLY function
 * that builds a body over this union, dropping the member makes the write
 * unsendable rather than merely unrendered — a stray `ownerId: null` CLEARS the
 * column with a 200 and nothing to restore from.
 */
type StaffKey = 'accountLeadId' | 'clientSuccessManagerId';

/* Both deliberately loose, and both copied verbatim from the modal: the server
 * checks the email format itself, and phone / website / linkedin / country code
 * are LENGTH-ONLY server-side, so a format rule on any of them here would
 * refuse values the API accepts. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The label a refusal names, so a message reads as the field the reader sees. */
const LABELS: Record<TextKey, string> = {
  name: CONTACT_NAME_LABELS.name,
  firstName: CONTACT_NAME_LABELS.firstName,
  lastName: CONTACT_NAME_LABELS.lastName,
  layout: 'Layout',
  title: 'Title',
  contactRole: 'Contact role',
  department: 'Department',
  email: 'Email',
  secondaryEmail: 'Secondary email',
  countryCode: 'Country code',
  mobile: 'Mobile',
  phone: 'Phone',
  website: 'Website',
  linkedin: 'LinkedIn',
  billingStreet: 'Street',
  billingCity: 'City',
  billingState: 'State',
  billingCode: 'Postal code',
  billingCountry: 'Country',
  shippingStreet: 'Street',
  shippingCity: 'City',
  shippingState: 'State',
  shippingCode: 'Postal code',
  shippingCountry: 'Country',
  description: 'Notes',
};

/**
 * The two states `emailOptOut` can hold, and there are only two.
 *
 * A picker rather than a text box because the column is COERCED, not nullish:
 * `null` becomes `false`, so an emptied control would silently re-subscribe
 * somebody who had opted out. Only a real boolean may be sent.
 */
const OPT_OUT_OPTIONS: InlineFieldOption[] = [
  { value: 'false', label: 'Subscribed to email' },
  { value: 'true', label: 'Opted out of email' },
];

/** The client-side rules, mirrored from `ContactFormModal.validate()`. */
function validateContactText(key: TextKey, next: string): string | null {
  if (next.length > TEXT_MAX[key]) {
    return `${LABELS[key]} must be ${TEXT_MAX[key]} characters or fewer.`;
  }
  if (key === 'name' && next === '') return 'Enter the contact’s full name.';
  if (key === 'firstName' && next === '') return 'Enter the contact’s first name.';
  /*
   * `layout` is `NOT NULL` and has a server default only on CREATE, so an
   * emptied box has nothing to mean here. The modal treats a blank layout as
   * "no change"; silently doing nothing is the wrong shape for a control the
   * person just used, so this refuses instead and says why.
   */
  if (key === 'layout' && next === '') return 'Layout is required — it can’t be cleared.';
  if ((key === 'email' || key === 'secondaryEmail') && next !== '' && !EMAIL_PATTERN.test(next)) {
    return 'Enter a valid email address.';
  }
  return null;
}

/** `YYYY-MM-DD` or empty. A part-typed date reads back as `''`, which on this
 *  nullable column IS a clear — so only a malformed complete value is refused. */
function validateDateOfBirth(next: string): string | null {
  if (next !== '' && !DATE_PATTERN.test(next)) return 'Use the format YYYY-MM-DD.';
  return null;
}

/**
 * The body for one text column, and nothing else.
 *
 * ⚠️ `''` maps to `null`, never to `''`: an empty string is a VALUE on this
 * API. `name` and `layout` are the two exceptions — both `NOT NULL`, both kept
 * non-empty by the validator above, so both are sent as text and never as null.
 */
function contactPatch(key: TextKey, next: string): ContactInput {
  if (key === 'name') return { name: next };
  if (key === 'layout') return { layout: next };
  const patch: ContactInput = {};
  patch[key] = next === '' ? null : next;
  return patch;
}

/** One staff id, written out by hand so a typo is a compile error. */
function staffPatch(key: StaffKey, next: string): ContactInput {
  const id = next === '' ? null : Number(next);
  if (key === 'accountLeadId') return { accountLeadId: id };
  return { clientSuccessManagerId: id };
}

/** The same value read back off the saved record, for the applied-check. */
function readStaff(saved: Contact, key: StaffKey): string {
  const person = key === 'accountLeadId' ? saved.accountLead : saved.clientSuccessManager;
  return person ? String(person.id) : '';
}

/*
 * ⚠️ *SUPERSEDED 2026-10-16:* `textRoundTrips` skipped the applied-check on a
 * cleared name part, on the belief that the API re-split `name` into an empty
 * part. `contacts.service.ts` does no such split — a null part is stored as
 * null — and First name can no longer be cleared at all, so every text write
 * is now read back.
 */

/** The parts as the screen shows them: stored, or `splitFullName`'s guess when neither is. */
function displayedNameParts(contact: Contact): { firstName: string; lastName: string } {
  const firstName = contact.firstName?.trim() ?? '';
  const lastName = contact.lastName?.trim() ?? '';
  if (firstName || lastName) return { firstName, lastName };
  return splitFullName(contact.name);
}

/**
 * The three name fields after an edit to one of them, by THE two-way rule
 * (`applyNameEdit`): First / Last rewrite Full name, Full name re-splits into
 * First / Last — only while the three agreed beforehand, so a deliberate
 * mismatch ("Dr. A. R. Menon") is kept. The "current" parts are what the
 * screen shows, including `splitFullName`'s guess on a legacy row.
 */
function nameEditResult(contact: Contact, key: ContactNameField, next: string): ContactNameParts {
  const shown = displayedNameParts(contact);
  return applyNameEdit({ ...shown, name: contact.name ?? '' }, key, next);
}

/**
 * The body for an edit to First, Last or Full name.
 *
 * Every key whose value differs from what is STORED is sent, and nothing
 * else — so a part that was only a guess on screen is stored along with the
 * change (what the person saw is what gets saved), and an unchanged key never
 * rides along. Never a blank `name` or `firstName` (both required); a blank
 * `lastName` is `null`.
 *
 * One PATCH carries every changed key, so the three can never be half-saved.
 */
function nameEditPatch(contact: Contact, key: ContactNameField, next: string): ContactInput {
  const after = nameEditResult(contact, key, next);
  const patch: ContactInput = {};
  if (after.firstName !== '' && after.firstName !== (contact.firstName?.trim() ?? '')) {
    patch.firstName = after.firstName;
  }
  if (after.lastName !== (contact.lastName?.trim() ?? '')) {
    patch.lastName = after.lastName === '' ? null : after.lastName;
  }
  if (after.name !== '' && after.name !== contact.name) patch.name = after.name;
  // Unreachable in practice (InlineField never saves an unchanged value), but
  // an empty PATCH is a 422, so the typed key is the floor.
  return Object.keys(patch).length > 0 ? patch : contactPatch(key, next);
}

/**
 * The length rules for the fields an edit DERIVES, so a full name whose split
 * would overflow First name (or parts whose join would overflow Full name) is
 * refused here rather than as a 422 on a key the person did not type into.
 */
function validateNameEdit(contact: Contact, key: ContactNameField, next: string): string | null {
  const after = nameEditResult(contact, key, next);
  for (const field of ['firstName', 'lastName', 'name'] as const) {
    if (field !== key && after[field].length > TEXT_MAX[field]) {
      return `${LABELS[field]} would be longer than ${TEXT_MAX[field]} characters.`;
    }
  }
  return null;
}

/* --- description-list scaffolding ---------------------------------------- */

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-4 py-4 sm:px-5">
      <h3 className="m-0 text-xs font-semibold text-fg-secondary">
        {title}
      </h3>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
        {children}
      </dl>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs text-fg-muted sm:pt-px">{label}</dt>
      <dd className="m-0 min-w-0 text-sm text-fg [overflow-wrap:anywhere]">{children}</dd>
    </>
  );
}

/* --- loading + terminal states ------------------------------------------- */

function DetailSkeleton() {
  return (
    <>
      <div className="pb-5">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
      <Panel>
        <div className="border-b border-border px-4 py-3">
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 3 }, (_, section) => (
            <div key={section} className="px-4 py-4 sm:px-5">
              <Skeleton className="h-3 w-28" />
              <div className="mt-3 grid gap-3.5">
                {Array.from({ length: 4 }, (_, row) => (
                  <div key={row} className="grid gap-2 sm:grid-cols-[11rem_1fr] sm:gap-6">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-52" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

function BackLink() {
  return (
    <TextLink
      to="/contacts"
      tone="muted"
      className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium"
    >
      <ChevronLeftIcon />
      All contacts
    </TextLink>
  );
}

/**
 * Reached both for a 404 and for a path segment that isn't a number at all.
 * Either way the record cannot exist, and the route back is what the user
 * actually needs.
 */
function NotFoundView() {
  const navigate = useNavigate();
  return (
    <>
      <BackLink />
      <Panel>
        <EmptyState
          icon={<ContactIcon />}
          title="Contact not found"
          description="This contact doesn’t exist, or has been deleted."
          action={
            <Button variant="primary" onClick={() => navigate('/contacts')}>
              Back to contacts
            </Button>
          }
        />
      </Panel>
    </>
  );
}

/* --- page ---------------------------------------------------------------- */

/**
 * The movable sections, in today's order and widths (each full width, stacked).
 * Ids are stored per person in `preferences.layouts.contact` — never rename one.
 */
const CONTACT_LAYOUT_DEFAULTS: readonly LayoutBlockDefault[] = [
  { id: 'details', label: 'Details' },
  { id: 'marketing', label: 'B2B Marketing' },
];

type LoadResult =
  | { kind: 'ready'; contact: Contact }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string };

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { ability } = useAuth();

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook. See that return, further down, for the reasoning.
   */
  const canReadModule = ability.canRead('contacts');

  /*
   * One state slot stamped with the id it describes, rather than separate
   * `loading` / `error` / `notFound` flags. Two payoffs: navigating straight
   * from one contact to another can never flash the previous record under the
   * new id, and the effect needs no synchronous `setState` to enter the loading
   * state — "no result for this id yet" is derived, so loading is just the
   * absence of `current`.
   */
  const [result, setResult] = useState<{ id: number; value: LoadResult } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /**
   * A 403 from an inline write, shown as a PAGE-level block.
   *
   * A 403 body carries no `details`, so it belongs to no field — and it is
   * never about the value that was typed, it is about the reader. The field
   * still reports its own outcome (a control that just did nothing has to say
   * so), and this says the thing that is true of the whole record: every other
   * field is about to refuse too.
   */
  const [writeDenied, setWriteDenied] = useState<string | null>(null);
  /** Two quick saves can resolve out of order; only the newest may be applied. */
  const writeSeq = useRef(0);

  // A non-numeric path segment can never match a record, so it is a 404 without
  // asking the API.
  const contactId = id !== undefined && /^\d+$/.test(id) ? Number(id) : null;
  const current = result && result.id === contactId ? result.value : null;

  // Editing a contact takes any write role; deleting one is admin-only, which
  // is what `canDelete` already encodes.
  const canUpdate = ability.canUpdate('contacts');
  const canDelete = ability.canDelete('contacts');
  /*
   * `withGroups` is on because BOTH staff pickers are ENFORCED by a user group:
   * naming somebody outside `account_lead` as `accountLeadId` is a 422 on
   * `/contacts`, create and update alike.
   *
   * ⚠️ **`users` — the un-narrowed directory — is no longer destructured.** It
   * was read by the Owner picker alone, that being the one staff field no
   * server guard checked and so the one that could not be narrowed; the picker
   * was removed on 2026-08-31. The flags below are hook-level and still
   * describe both narrowed lists.
   *
   * Gated on `canUpdate`: a reader who cannot write sees the stored person as
   * text and never opens a picker, so the request would buy nothing.
   */
  /*
   * ⚠️ `failed` and `hasMore` are no longer destructured — both existed solely
   * to word sentences removed under the owner's stricter helper-text ruling
   * (2026-09-05): a load-failure notice and a truncation notice.
   */
  const { loading: usersLoading, byGroup } = useUserOptions(
    contactId !== null && canUpdate,
    true,
  );

  // Above every early return so the hook order never changes.
  const layout = usePageLayout('contact', CONTACT_LAYOUT_DEFAULTS);

  useEffect(() => {
    if (contactId === null) return;

    const controller = new AbortController();
    const settle = (value: LoadResult) => setResult({ id: contactId, value });

    api
      .getContact(contactId, controller.signal)
      .then((response) => settle({ kind: 'ready', contact: response.data }))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) {
          settle({ kind: 'notfound' });
          return;
        }
        settle({
          kind: 'error',
          message: caught instanceof Error ? caught.message : 'Failed to load this contact',
        });
      });

    return () => controller.abort();
  }, [contactId, reloadToken]);

  async function handleDelete() {
    if (contactId === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteContact(contactId);
      navigate('/contacts', { replace: true });
    } catch (caught) {
      // The role could have changed mid-session, so a 403 is possible even
      // though the button is gated on it. A 403 body has no `details`, so this
      // stays a banner; `denialReason` is null in exactly that stale-token case.
      if (caught instanceof ApiError && caught.status === 403) {
        setDeleteError(
          denialReason(ability, 'delete', 'contacts') ??
            'You no longer have permission to delete contacts.',
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        setDeleteError('This contact has already been deleted.');
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
      setDeleting(false);
    }
  }

  /**
   * Writes ONE column and reports what happened, in the vocabulary
   * `InlineField` renders. Nothing here throws — the field needs an outcome,
   * not an exception.
   *
   * `readBack` is how a save is verified. `PATCH /contacts/:id` is `.strict()`,
   * so a 2xx proves the key was RECOGNISED — it proves nothing about it having
   * been APPLIED (a derived column, a guard that ignored it, a write that
   * landed elsewhere all answer 200). Passing `null` is the explicit way to say
   * "this value legitimately does not come back as it was sent"; see
   * `textRoundTrips`.
   */
  const saveContactField = async (
    requestKey: string,
    patch: ContactInput,
    readBack: { sent: string; read: (saved: Contact) => string; noun: string } | null,
  ): Promise<InlineSaveResult> => {
    if (contactId === null) {
      return { status: 'error', message: 'This contact could not be identified.' };
    }
    const ticket = writeSeq.current + 1;
    writeSeq.current = ticket;

    try {
      const response = await api.updateContact(contactId, patch);
      const saved = response.data;

      // Only the newest write may land: an earlier one resolving late would
      // put a stale record back on screen under a fresher value.
      if (writeSeq.current === ticket) {
        setResult({ id: contactId, value: { kind: 'ready', contact: saved } });
        setWriteDenied(null);
      }

      if (readBack && readBack.read(saved) !== readBack.sent) {
        return {
          status: 'warning',
          message: `${readBack.noun} not saved — the server didn’t apply the change.`,
        };
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          /*
           * The control is gated on `canUpdate`, but a role can be taken away
           * under an access token that lasts about five days — so the gate is
           * the optimisation and this is the guarantee. Raised at page level
           * too: it is true of every field, not of this one.
           */
          const reason =
            denialReason(ability, 'update', 'contacts') ??
            'You no longer have permission to edit contacts.';
          setWriteDenied(reason);
          return { status: 'error', message: `${reason} Nothing was saved.` };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This contact no longer exists, so the change could not be saved.',
          };
        }
        /*
         * Ordered by SPECIFICITY, never by which shape arrived first: the 422
         * names the request key exactly, so it lands on the control that caused
         * it. `formMessage` catches the two that bind to no field — a
         * `.strict()` rejection and the empty-PATCH refusal, both carrying an
         * empty path — and `message` is last because on a 422 it is only
         * "Validation failed".
         */
        const fieldMessage = caught.fieldErrors[requestKey];
        if (fieldMessage) return { status: 'error', message: fieldMessage };
        const formMessage = caught.formMessage;
        if (formMessage) return { status: 'error', message: formMessage };
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Couldn’t reach the server. Check your connection.',
      };
    }
  };

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and error branches, deliberately. A reader
   * who may not see contacts must not be told whether this one exists: "Contact
   * not found" over a record that is simply hidden from them is a wrong answer
   * dressed as a real one.
   *
   * ⚠️ **A detail page needs the same gate as its list.** `/contacts/:id` open
   * while `/contacts` refuses is the same hole one level down, and a detail page
   * is exactly what a stale bookmark or a link in an email points at.
   *
   * ⚠️ This DEGRADES OPEN — only an explicit `none` scope denies; see `can()` in
   * `@/lib/permissions`. And **hiding a page is not refusing a request**:
   * `GET /contacts/:id` answers the same to everybody, so nothing here may be
   * worded as though the record were protected. This is a view control.
   *
   * No back link: `/contacts` answers this reader with the same refusal, so
   * offering it would send them one click further into the same sentence.
   */
  if (!canReadModule) {
    return (
      <Panel>
        <EmptyState
          icon={<ContactIcon />}
          title="This page isn’t available to your account"
          description="Your permission set does not include this. An administrator can change that."
        />
      </Panel>
    );
  }

  if (contactId === null) return <NotFoundView />;

  if (current === null) {
    return (
      <>
        <BackLink />
        <DetailSkeleton />
      </>
    );
  }

  if (current.kind === 'notfound') return <NotFoundView />;

  if (current.kind === 'error') {
    return (
      <>
        <BackLink />
        <Panel>
          <div className="border-b border-border px-4 py-2">
            <Notice tone="danger" role="alert" title="Couldn’t load this contact.">
              <span className="min-w-0 [overflow-wrap:anywhere]">{current.message}</span>
            </Notice>
          </div>
          <PanelBody>
            <Button variant="secondary" size="sm" onClick={() => setReloadToken((n) => n + 1)}>
              Try again
            </Button>
          </PanelBody>
        </Panel>
      </>
    );
  }

  const { contact } = current;

  /*
   * Why the fields are mounted UNCONDITIONALLY and gate themselves.
   *
   * `{canUpdate && <InlineField/>}` would hide the VALUE from everybody who
   * cannot write it — and read on `/contacts` is open to every role, `viewer`
   * included. `disabled` plus `disabledReason` renders the value as plain text
   * with the reason under it, so a reader sees the record and why it is inert
   * rather than an absence.
   */
  const editDenial = canUpdate ? undefined : (denialReason(ability, 'update', 'contacts') ?? undefined);

  /*
   * Built by plain functions returning elements, never components declared in
   * here — a component declared inside this one is a new type on every render,
   * so every field would unmount (losing its draft and its "Saved" line)
   * whenever anything else on the page changed.
   */
  const textField = (
    key: TextKey,
    extra: {
      display?: ReactNode;
      action?: ReactNode;
      emptyLabel?: string;
      type?: 'text' | 'email' | 'tel';
      inputMode?: 'text' | 'url';
      autoComplete?: string;
      placeholder?: string;
      multiline?: boolean;
      rows?: number;
    } = {},
  ) => {
    const { multiline, ...rest } = extra;
    const isPart = key === 'firstName' || key === 'lastName';
    const isName = isPart || key === 'name';
    const stored = isPart ? displayedNameParts(contact)[key] : (contact[key] ?? '');
    return (
      <InlineField
        label={LABELS[key]}
        value={stored}
        kind={multiline ? 'multiline' : 'text'}
        disabled={!canUpdate}
        {...(editDenial === undefined ? {} : { disabledReason: editDenial })}
        maxLength={TEXT_MAX[key]}
        validate={(next) =>
          validateContactText(key, next) ?? (isName ? validateNameEdit(contact, key, next) : null)
        }
        onSave={(next) =>
          saveContactField(
            key,
            isName ? nameEditPatch(contact, key, next) : contactPatch(key, next),
            { sent: next, read: (saved) => saved[key] ?? '', noun: LABELS[key].toLowerCase() },
          )
        }
        {...rest}
      />
    );
  };

  /**
   * A picker over the staff directory.
   *
   * ⚠️ The stored holder is ALWAYS injected when the offered list does not
   * carry them — whether that is because they sit past the first 100 users or
   * because they are outside the enforcing group. A picker that dropped them
   * would read as "Unassigned", and one stray save would clear a field nobody
   * touched. `outsideGroupNote` labels them, and returns null while the list is
   * unknown so a perfectly valid lead is never accused on the strength of a
   * request that has not come back.
   */
  const staffField = (key: StaffKey, label: string, person: UserSummary | null) => {
    const staff =
      key === 'accountLeadId'
        ? byGroup[USER_GROUP_SLUGS.accountLead]
        : byGroup[USER_GROUP_SLUGS.clientSuccessManager];

    const options: InlineFieldOption[] = staff.users.map((user) => ({
      value: String(user.id),
      label: user.name,
      hint: user.email,
    }));
    if (person && !staff.users.some((user) => user.id === person.id)) {
      const outside = outsideGroupNote(staff);
      options.unshift({
        value: String(person.id),
        label: person.name,
        hint: [person.email, outside].filter(Boolean).join(' · '),
      });
    }

    /*
     * Independent facts about one list, so each is said rather than one
     * standing in for the others.
     *
     * ⚠️ **DOWN TO ONE — owner's stricter helper-text ruling, 2026-09-05.** Two
     * of the three went: "Staff list unavailable — this picker is empty for that
     * reason." (a load failure; the picker is not disabled by it, only empty)
     * and "Showing the first 100 users." (a truncation notice).
     *
     * ⚠️ What SURVIVES is `narrowingNote`, and it is a refusal: naming somebody
     * outside `account_lead` is a live 422 on `/contacts` as well as on
     * `/companies`. The array shape is kept rather than collapsed to a string —
     * a second refusal would join it, and a `??` between two of them is the
     * silent drop this codebase keeps meeting.
     */
    const notes = [narrowingNote(staff)].filter((note): note is string => note !== null);

    return (
      <div className="grid min-w-0 gap-1">
        <InlineField
          kind="picker"
          label={label}
          value={person ? String(person.id) : ''}
          display={<StaffValue user={person} linked={!canUpdate} />}
          disabled={!canUpdate}
          {...(canUpdate && person && person.email !== ''
            ? { action: <ValueAction value={person.email} kind="email" /> }
            : {})}
          {...(editDenial === undefined ? {} : { disabledReason: editDenial })}
          options={options}
          optionsLoading={usersLoading}
          emptyOptionLabel="Unassigned"
          placeholder={usersLoading ? 'Loading users…' : 'Unassigned'}
          searchPlaceholder="Search staff…"
          {...(notes.length > 0 ? { footnote: notes.join(' ') } : {})}
          onSave={(next) =>
            saveContactField(key, staffPatch(key, next), {
              sent: next,
              read: (saved) => readStaff(saved, key),
              noun: label.toLowerCase(),
            })
          }
        />
        {/*
          "Staff list unavailable — this picker is empty." stood here and is
          GONE — owner's stricter helper-text ruling, 2026-09-05. A load failure,
          not a refusal, over a picker that is empty but not disabled.

          ⚠️ Its own comment recorded the cost and it still applies: a reader now
          sees an ordinary "Unassigned" over a list that never loaded.
        */}
      </div>
    );
  };

  return (
    <>
      <BackLink />

      {/*
        Not PageHeader: the supporting line carries a company link and a Badge,
        and PageHeader renders `description` inside a <p>, where a Badge's
        <span>-in-<p> is fine but the whole line needs its own flex layout.
      */}
      <div className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <h1 className="m-0 text-2xl font-semibold tracking-[-0.01em] text-fg [overflow-wrap:anywhere]">
            {contact.name}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-fg-muted">
            {contact.title && <span className="[overflow-wrap:anywhere]">{contact.title}</span>}
            {contact.title && <span aria-hidden="true">·</span>}
            <CompanyValue company={contact.company} />
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canUpdate && (
            <Button
              variant="secondary"
              leadingIcon={<PencilIcon />}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              leadingIcon={<TrashIcon />}
              onClick={() => {
                setDeleteError(null);
                setConfirmOpen(true);
              }}
            >
              Delete
            </Button>
          )}
          <LayoutToolbar layout={layout} />
        </div>
      </div>

      <LayoutGrid
        layout={layout}
        render={{
          details: () => (
      <Panel aria-labelledby="contact-details-heading">
        <PanelHeader id="contact-details-heading" title="Details" />

        {/*
          A 403 carries no `details`, so it belongs to no field and renders
          here. It survives until the next successful write rather than until
          the next render: a refusal that clears itself is a refusal nobody
          reads.
        */}
        {writeDenied && (
          <div className="border-b border-border px-4 py-2 sm:px-5">
            <Notice tone="danger" role="alert" title="That change wasn’t saved.">
              <span className="min-w-0 [overflow-wrap:anywhere]">{writeDenied}</span>
            </Notice>
          </div>
        )}

        {/* divide-y rather than a border-t per Section: the first one sits
            directly under PanelHeader's border-b and would double the hairline. */}
        <div className="divide-y divide-border">
          <Section title="Overview">
            {/*
              First name · Last name · Full name, the order every contact form
              uses (owner, 2026-10-16). They fill each other both ways while
              they agree (a part rewrites Full name; Full name re-splits into
              the parts) — see `nameEditPatch`.
              ⚠️ *SUPERSEDED:* both parts carried the empty label "Split from
              the full name"; a contact with no stored parts now shows
              `splitFullName`'s guess, which saving either part stores.
            */}
            <Row label={LABELS.firstName}>
              {textField('firstName', { autoComplete: 'given-name' })}
            </Row>
            <Row label={LABELS.lastName}>
              {textField('lastName', { autoComplete: 'family-name' })}
            </Row>
            <Row label={LABELS.name}>
              {textField('name', { autoComplete: 'name' })}
            </Row>
            {/*
              READ-ONLY, and not a disabled control: re-parenting a contact
              needs the debounced server-side account search that lives in the
              Edit dialog, and it moves the derived `accountLead` with it.
            */}
            <Row label="Account">
              <CompanyValue company={contact.company} />
            </Row>
            <Row label="Title">
              <ContactTitleInlineField
                label={LABELS.title}
                value={contact.title ?? ''}
                disabled={!canUpdate}
                {...(editDenial === undefined ? {} : { disabledReason: editDenial })}
                validate={(next) => validateContactText('title', next)}
                onSave={(next) =>
                  saveContactField('title', contactPatch('title', next), {
                    sent: next,
                    read: (saved) => saved.title ?? '',
                    noun: LABELS.title.toLowerCase(),
                  })
                }
              />
            </Row>
            <Row label="Email">
              {textField('email', {
                type: 'email',
                autoComplete: 'email',
                action: <ValueAction value={contact.email} kind="email" />,
              })}
            </Row>
            <Row label="Mobile">
              {textField('mobile', {
                type: 'tel',
                action: (
                  <ValueAction
                    value={contact.mobile}
                    kind="tel"
                    countryCode={contact.countryCode}
                  />
                ),
              })}
            </Row>
            {/*
              ⚠️ THE OWNER ROW STOOD HERE; THE SLOT IS DELIBERATELY EMPTY.
              Owner is gone from contacts (owner's instruction, 2026-08-31) and
              `ownerId` is not a member of `StaffKey`, so `staffPatch` cannot
              build a body naming it. Account lead — a DIFFERENT person, not a
              rename — is in the Relationship section.
            */}
            {/* `layout` is NOT NULL: the validator refuses an empty box rather
                than sending a clear the column cannot take. */}
            <Row label="Layout">{textField('layout')}</Row>
          </Section>

          <Section title="Role and ownership">
            <Row label="Contact role">
              {textField('contactRole')}
            </Row>
            <Row label="Department">{textField('department')}</Row>
            <Row label="Account lead">
              {staffField('accountLeadId', 'Account lead', contact.accountLead)}
            </Row>
            <Row label="Client success manager">
              {staffField(
                'clientSuccessManagerId',
                'Client success manager',
                contact.clientSuccessManager,
              )}
            </Row>
            {/*
              ⚠️ READ-ONLY, DELIBERATELY — do not make this an inline field.
              What is displayed is `leadSourceRef.name`, the resolved row. The
              only WRITABLE key is `leadSource`, the deprecated free text, and
              the two legitimately differ: the row names the category
              ("Conference") while the text holds what somebody typed
              ("Conference — NAFSA 2026"). An editor showing the label and
              saving to the text would write the category over the detail on the
              next save — silently, with a plausible-looking string, which is
              the `[object Object]` incident with its loud tell filed off.
              `ContactInput` carries no `leadSourceId`, so there is nothing
              correct to bind an editor to yet. See `src/lib/lead-source.ts`.

              ⚠️ `orDash(contact.leadSource)` stood here and would have printed
              `[object Object]` from 2026-08-27, when the field became a
              resolved `{id, name}` row. It was caught by `tsc` only because the
              type was widened to the union rather than swapped to the object —
              a swap would have compiled here and been wrong on screen.
            */}
            <Row label="Lead source">{orDash(leadSourceLabel(contact))}</Row>
          </Section>

          <Section title="Contact details">
            <Row label="Secondary email">
              {textField('secondaryEmail', {
                type: 'email',
                action: <ValueAction value={contact.secondaryEmail} kind="email" />,
              })}
            </Row>
            <Row label="Country code">{textField('countryCode')}</Row>
            <Row label="Phone">
              {textField('phone', {
                type: 'tel',
                action: (
                  <ValueAction value={contact.phone} kind="tel" countryCode={contact.countryCode} />
                ),
              })}
            </Row>
            <Row label="Website">
              {textField('website', {
                inputMode: 'url',
                action: <ValueAction value={contact.website} kind="url" />,
              })}
            </Row>
            <Row label="LinkedIn">
              {textField('linkedin', {
                inputMode: 'url',
                action: <ValueAction value={contact.linkedin} kind="url" />,
              })}
            </Row>
          </Section>

          <Section title="Personal">
            {/*
              `toDateInputValue` slices the date part; the column serialises as
              a full ISO timestamp and a native date input handed one renders
              BLANK, which reads as "no date" on a record that has one.
              `formatDateOnly` then formats in UTC on purpose — localising a
              date-only value drifts a day west of UTC.
            */}
            <Row label="Date of birth">
              <InlineField
                label="Date of birth"
                type="date"
                value={toDateInputValue(contact.dateOfBirth)}
                emptyLabel="Not recorded"
                disabled={!canUpdate}
                {...(editDenial === undefined ? {} : { disabledReason: editDenial })}
                validate={validateDateOfBirth}
                display={
                  contact.dateOfBirth === null ? (
                    <span className="text-fg-subtle">Not recorded</span>
                  ) : (
                    <span className="tabular">{formatDateOnly(contact.dateOfBirth)}</span>
                  )
                }
                onSave={(next) =>
                  saveContactField(
                    'dateOfBirth',
                    // `null` clears; `''` is a value on this API and must never
                    // travel as one.
                    { dateOfBirth: next === '' ? null : next },
                    {
                      sent: next,
                      read: (saved) => toDateInputValue(saved.dateOfBirth),
                      noun: 'date of birth',
                    },
                  )
                }
              />
            </Row>
            {/*
              A two-option picker, not a text box: `emailOptOut` is COERCED
              rather than nullish server-side, so `null` becomes `false` — a
              cleared box would quietly re-subscribe somebody who had opted out.
              Only a real boolean can be sent, and only two exist.
            */}
            <Row label="Email preference">
              <InlineField
                kind="picker"
                label="Email preference"
                value={contact.emailOptOut ? 'true' : 'false'}
                options={OPT_OUT_OPTIONS}
                disabled={!canUpdate}
                {...(editDenial === undefined ? {} : { disabledReason: editDenial })}
                searchPlaceholder="Filter…"
                display={<OptOutValue optedOut={contact.emailOptOut} />}
                onSave={(next) => {
                  const emailOptOut = next === 'true';
                  return saveContactField(
                    'emailOptOut',
                    { emailOptOut },
                    {
                      sent: next,
                      read: (saved) => (saved.emailOptOut ? 'true' : 'false'),
                      noun: 'email preference',
                    },
                  );
                }}
              />
            </Row>
          </Section>

          {/*
            Five rows apiece rather than the single rendered block that stood
            here. A block cannot be edited a column at a time, and one control
            over five columns would have to send all five on every save — which
            on an API where `null` clears is how four untouched columns get
            wiped by a correction to the fifth.
          */}
          <Section title="Billing address">
            <Row label="Street">
              {textField('billingStreet', { autoComplete: 'off', emptyLabel: 'Not recorded' })}
            </Row>
            <Row label="City">{textField('billingCity', { emptyLabel: 'Not recorded' })}</Row>
            <Row label="State">{textField('billingState', { emptyLabel: 'Not recorded' })}</Row>
            <Row label="Postal code">
              {textField('billingCode', { emptyLabel: 'Not recorded' })}
            </Row>
            <Row label="Country">{textField('billingCountry', { emptyLabel: 'Not recorded' })}</Row>
          </Section>

          <Section title="Shipping address">
            <Row label="Street">
              {textField('shippingStreet', { autoComplete: 'off', emptyLabel: 'Not recorded' })}
            </Row>
            <Row label="City">{textField('shippingCity', { emptyLabel: 'Not recorded' })}</Row>
            <Row label="State">{textField('shippingState', { emptyLabel: 'Not recorded' })}</Row>
            <Row label="Postal code">
              {textField('shippingCode', { emptyLabel: 'Not recorded' })}
            </Row>
            <Row label="Country">
              {textField('shippingCountry', { emptyLabel: 'Not recorded' })}
            </Row>
          </Section>

          <Section title="Notes">
            <Row label="Description">
              {textField('description', {
                multiline: true,
                rows: 5,
                emptyLabel: '—',
                display: contact.description ? (
                  <span className="block whitespace-pre-wrap">{contact.description}</span>
                ) : (
                  <span className="text-fg-subtle">—</span>
                ),
              })}
            </Row>
          </Section>
        </div>

        <div className="flex flex-col gap-1.5 border-t border-border bg-surface-sunken px-4 py-3 text-xs text-fg-muted sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <span className="[overflow-wrap:anywhere]">
            Created {formatDateTime(contact.createdAt)}
            {contact.createdBy ? ` by ${contact.createdBy.name}` : ''}
          </span>
          <span className="[overflow-wrap:anywhere]">
            Updated {formatDateTime(contact.updatedAt)}
            {contact.modifiedBy ? ` by ${contact.modifiedBy.name}` : ''}
          </span>
        </div>
      </Panel>
          ),
          marketing: () => (
            <div className="min-w-0">
              <MarketingHistoryPanel contactId={contact.id} />
            </div>
          ),
        }}
      />

      <ContactFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        contact={contact}
        onSaved={(saved) => {
          setResult({ id: saved.id, value: { kind: 'ready', contact: saved } });
          setEditOpen(false);
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteError(null);
        }}
        onConfirm={() => void handleDelete()}
        title={`Delete ${contact.name}?`}
        description={
          <>
            This permanently removes {contact.name}
            {contact.company ? ` from ${contact.company.name}` : ''} and everything recorded against
            them. This can’t be undone.
          </>
        }
        confirmLabel="Delete contact"
        loading={deleting}
        error={deleteError}
      />
    </>
  );
}
